import { useState } from 'react';
import { Loader2, Send, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import SEO from '../SEO';

const N8N_BASE = process.env.REACT_APP_N8N_BASE_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setError('Please fill out all fields.');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!N8N_BASE) {
      setError('Contact form is not configured. Please email hello@kzcomp.com directly.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${N8N_BASE}/webhook/contact-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          message: trimmedMessage,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Request failed (${res.status})`);
      }
      toast.success("Message sent. We'll be in touch.");
      setSent(true);
      setName('');
      setEmail('');
      setMessage('');
    } catch (err) {
      console.error('Contact submit error:', err);
      setError(err.message || 'Could not send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col px-4 py-12 sm:px-6">
      <SEO
        title="Contact KZCOMP"
        description="Get in touch with the KZCOMP team — tournament inquiries, partnerships and feedback."
        canonicalPath="/contact"
      />
      <div className="mb-8 flex flex-col gap-2">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-primary" />
          Get in touch
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Contact</h1>
        <p className="text-sm text-muted-foreground">
          Questions, feedback, partnership ideas — drop us a line and we'll reply by email.
        </p>
      </div>

      {sent ? (
        <div className="flex flex-col items-start gap-4 rounded-lg border border-border/60 bg-card p-6 shadow-sm">
          <MailCheck className="h-8 w-8 text-success" />
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-medium">Thanks — we got it.</h2>
            <p className="text-sm text-muted-foreground">
              A confirmation has been sent to your email. We usually reply within a couple of days.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setSent(false)}>
            Send another
          </Button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-5 rounded-lg border border-border/60 bg-card p-6 shadow-sm"
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              placeholder="Your name"
              maxLength={120}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              placeholder="you@example.com"
              maxLength={200}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              placeholder="What's on your mind?"
              rows={6}
              maxLength={5000}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? 'Sending…' : 'Send message'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default Contact;
